import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { ThemeProvider } from '@material-ui/core/styles';
import { Tabs, Tab } from '@material-ui/core';
import { InputBase, Paper, IconButton, TextField, MenuItem, Button } from '@material-ui/core';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import SaveAltIcon from '@material-ui/icons/SaveAlt';

import { setSnackbar } from '../redux/workBackendSlice';
import { SheetKinds } from '../redux/sheetDbSlice';
import { addNote, updateNote } from '../redux/noteDbSlice';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useCurrentDb from '../hooks/useCurrentDb';
import useNoteDbByReference from '../hooks/useNoteDbByReference';
import useMakeExportText from '../hooks/useMakeExportText';
import useElementDimensions from '../hooks/useElementDimensions';
import useAllPaletteDbs from '../hooks/useAllPaletteDbs';
import usePaletteCardTemplateSet from '../hooks/usePaletteCardTemplateSet';
import useAllCardDbsInSheet from '../hooks/useAllCardDbsInSheet';
import useAddCardExe from '../hooks/useAddCardExe';

import { CoordinateType, getTimestamp } from '../general';
import TabPanel, { TabsAppBar, tabsIndicatorTheme, a11yProps } from './TabPanel';
import { CardsGridList } from './AddCardDialog';

const NoteWrapper = styled.div`
  height: 100%;
  overflow: auto;
  cursor: text;
  box-sizing: border-box;
  border: 1px #eeeeee;
  border-style: none none none solid;
`
const NoteInputBase = styled(InputBase)`
  position: relative;
  width: 100%;
  z-index: 1;
`
const ExportTextPaper = styled(Paper)`
  height: 96%;
  overflow: hidden;
`
const ExportTextHeader = styled.div`
  position: sticky;
  top: 48px;
  padding: 4px 8px 4px 8px;
  background-color: #b5b5b5;
  display: flex;
  align-items: center;
`
const ExportTextWrapper = styled.div`
  margin: 8px;
  word-break: break-all;
`
const ToggleButton = styled.div`
  position: absolute;
  top: 44%;
  height: 64px;
  background-color: #838383;
  opacity: 0.2;
  border-radius: 0 8px 8px 0;
  z-index: 2;
  cursor: pointer;
  transition: 180ms;
  :hover {
    opacity: 0.5;
  };
`
const OpenNoteButtonDiv = styled.div`
  position: absolute;
  right: 0px;
  top: 40%;
  height: 64px;
  background-color: #838383;
  opacity: 0.2;
  border-radius: 8px 0 0 8px;
  z-index: 2;
  cursor: pointer;
  transition: 180ms;
  :hover {
    opacity: 0.5;
  };
`

export type ReferenceType = 'book' | 'sheet' | 'cardTemplate' | 'card'
const holderTexts: { [key in ReferenceType]: string } = {
  'book': 'このブックの作業メモを書くことができます',
  'sheet': 'このシートの作業メモを書くことができます',
  'cardTemplate': 'このカードテンプレートの作業メモを書くことができます',
  'card': 'このカードの作業メモを書くことができます'
}
const getReferenceId = (reference: ReferenceType, bookId: string | null, sheetId: string | null, referenceId?: string): string | undefined => {
  if (reference === 'book') return bookId || undefined;
  else if (reference === 'sheet') return sheetId || undefined;
  else return referenceId;
}

type OpenNoteButtonProps = {
  noteDisplay: boolean;
  openNote: () => void;
}
export const OpenNoteButton: React.FC<OpenNoteButtonProps> = ({ noteDisplay, openNote }) => {
  if (noteDisplay) return <></>;
  return (
    <OpenNoteButtonDiv onClick={openNote}><ArrowBackIosIcon fontSize="small" style={{ marginLeft: 4, marginTop: 21 }} /></OpenNoteButtonDiv>
  )
}

type PaletteTabProps = {
  userId?: string;
  tab: number;
  width?: number;
  height?: number;
  selectingCardId?: string;
  setSelectingCardId: (value?: string) => void;
  dicision: () => void;
}
const PaletteTab: React.FC<PaletteTabProps> = (props) => {
  const { userId, tab, width, height, dicision, selectingCardId, setSelectingCardId } = props;
  const [paletteIds, palettes] = useAllPaletteDbs(userId);
  const paletteCardTemplateSet = usePaletteCardTemplateSet();
  const [selectingPaletteId, setSelectingPaletteId] = useState('lately');

  const changePalette = (value: string) => {
    setSelectingPaletteId(value);
    setSelectingCardId();
  }

  return (
    <TabPanel value={tab} index={0}>
      <TextField
        select variant="outlined" size="small"
        value={selectingPaletteId}
        onChange={ev => changePalette(ev.target.value)}
        style={{ marginTop: 8, marginLeft: 16 }}
      >
        <MenuItem value="lately">最近使ったカード</MenuItem>
        { paletteIds.map((id, ix) => {
          const palette = palettes[ix];
          if (!palette) return;
          return <MenuItem value={id}>{ palette.name }</MenuItem>;
        }) }
      </TextField>
      <CardsGridList editMode={false}
        paletteId={selectingPaletteId}
        selectingCardId={selectingCardId}
        cardClicked={(id: string) => setSelectingCardId(id)}
        cardDoubleClicked={() => dicision()}
        ids={paletteCardTemplateSet[selectingPaletteId] ? paletteCardTemplateSet[selectingPaletteId].ids : []}
        values={paletteCardTemplateSet[selectingPaletteId] ? paletteCardTemplateSet[selectingPaletteId].values : []}
        onEdit={(id) => undefined}
        onDelete={(paletteId, cardTemplateId) => undefined}
        onDuplicate={(id: string) => undefined}
        setDraggingId={() => undefined}
        changeCTOrder={(ev, id) => undefined}
        setTemplateValues={values => undefined}
        checkPaletteChanging={(ev, id) => undefined}
        invalidCardTransition={true}
        invalidEditBoxTransition={true}
        width={width} height={height ? height - 48 : undefined}
        setMenuAnchor={value => undefined}
      />
    </TabPanel>
  );
}

type NoteProps = {
  reference: ReferenceType;
  referenceId?: string;
  toggleNote: () => void;
  splitUpdated?: boolean;
  parent?: SheetKinds | string;
  index?: number;
  coordinate?: CoordinateType;
  grid?: CoordinateType;
  clearSelectingGrid?: () => void;
}
const Note: React.FC<NoteProps> = (props) => {
  const { reference, toggleNote, splitUpdated, ...addCardExeProps } = props;
  const dispatch = useDispatch();
  const userId = useCurrentUserId();
  const [sheetId, bookId] = useCurrentDb();
  const referenceId = getReferenceId(reference, bookId, sheetId, props.referenceId);
  const [noteId, note] = useNoteDbByReference(referenceId);
  const [exportText, exportName] = useMakeExportText(reference, referenceId);
  const [ids, cards] = useAllCardDbsInSheet(sheetId);
  const addCardExe = useAddCardExe();
  const [selectingCardId, setSelectingCardId] = useState<string>();
  const [tab, setTab] = useState(0);
  const [value, setValue] = useState('');
  const noteIx = reference === 'sheet' ? 1 : 0;
  const dataIx = reference === 'sheet' ? 2 : 1;
  const inputId = `note-input-base-${referenceId}`;
  const wrapperId = `note-wrapper-${referenceId}`;
  const { width, height } = useElementDimensions(wrapperId, splitUpdated);
  const graphStyle = {
    backgroundColor: '#ffffff',
    backgroundImage: 'linear-gradient(0deg, transparent calc(100% - 1px), #f0f0f0 calc(100% - 1px)), linear-gradient(90deg, transparent calc(100% - 1px), #f0f0f0 calc(100% - 1px))',
    backgroundSize: '24px 24px',
    backgroundRepeat: 'repeat',
    backgroundPosition: 'center center',
    backgroundAttachment: 'local'
  }

  const focusInput = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (tab !== noteIx) return;
    const input = document.getElementById(inputId);
    if (ev.target === input || !input || !('setSelectionRange' in input) || typeof input.setSelectionRange !== 'function') return;
    ev.preventDefault();
    input.focus();
    const ix = ev.clientY <= input.getBoundingClientRect().top ? 0 : value.length;
    input.setSelectionRange(ix, ix);
  }
  const updateContent = () => {
    if (noteId && note) {
      if (value !== note.content) dispatch(updateNote({ id: noteId, content: value }));
    } else if (userId && referenceId && value !== '') dispatch(addNote({ userId, referenceId, content: value }));
  }
  const changeTab = (value: number) => {
    updateContent();
    setTab(value);
  }
  const copyToClipboard = () => {
    navigator.permissions.query({ name: 'clipboard-write' as any }).then(res => {
      if (res.state === 'denied') {
        dispatch(setSnackbar({ open: true, message: 'コピーに失敗しました。'}));
        return;
      }
      navigator.clipboard.writeText(exportText).then(() => {
        dispatch(setSnackbar({ open: true, message: 'クリップボードにコピーしました。'}));
      });
    });
  }
  const download = () => {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([exportText], { type: 'text/plain' }));
    const name = exportName ? `-${exportName}` : '';
    const timestamp = getTimestamp();
    link.download = `${reference}${name}_${timestamp}.json`;
    link.click();
  }

  useEffect(() => {
    setTab(0);
    if (!note) setValue('');
    else setValue(note.content);
  }, [useNoteDbByReference(referenceId)[0]])

  return (
    <>
      <ThemeProvider theme={tabsIndicatorTheme}>
        <NoteWrapper id={wrapperId} onMouseDown={ev => focusInput(ev)}
          style={ tab === noteIx ? { ...graphStyle, overflowY: (tab !== noteIx && tab !== dataIx) ? 'hidden' : undefined } : { overflowY: (tab !== noteIx && tab !== dataIx) ? 'hidden' : undefined } }
        >
          <TabsAppBar position="static" elevation={0} style={{ position: 'sticky', top: 0, backgroundColor: 'inherit' }}>
            <Tabs value={tab} onChange={(ev, value) => changeTab(value)} variant="scrollable" scrollButtons="auto">
              { reference === 'sheet' && <Tab label="パレット" {...a11yProps(0)} /> }
              <Tab label="ノート" {...a11yProps(noteIx)} />
              { reference !== 'cardTemplate' && <Tab label="データ" {...a11yProps(dataIx)} /> }
            </Tabs>
          </TabsAppBar>
          <ToggleButton onClick={toggleNote}><ArrowForwardIosIcon fontSize="small" style={{ marginTop: 21 }} /></ToggleButton>
          { reference === 'sheet' &&
            <PaletteTab
              userId={userId}
              tab={tab}
              width={width}
              height={height}
              selectingCardId={selectingCardId}
              setSelectingCardId={setSelectingCardId}
              dicision={() => addCardExe({ ...addCardExeProps, selectingCardId, ids, cards })}
            />
          }
          <TabPanel value={tab} index={noteIx}>
            <NoteInputBase
              id={inputId}
              multiline
              placeholder={holderTexts[reference]}
              value={value}
              onChange={ev => setValue(ev.currentTarget.value)}
              onBlur={() => updateContent()}
            />
          </TabPanel>
          { reference !== 'cardTemplate' &&
            <TabPanel value={tab} index={dataIx}>
              <ExportTextHeader>
                <div style={{ width: '50%' }}>
                  JSON
                </div>
                <div style={{ width: '50%', display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton onClick={copyToClipboard}><FileCopyOutlinedIcon fontSize="small" /></IconButton>
                  <IconButton onClick={download}><SaveAltIcon fontSize="small" /></IconButton>
                </div>
              </ExportTextHeader>
              <ExportTextPaper elevation={0} square>
                <ExportTextWrapper>{ exportText }</ExportTextWrapper>
              </ExportTextPaper>
            </TabPanel>
          }
          { (tab !== noteIx && tab !== dataIx) &&
            <Button
              variant="outlined"
              color="primary"
              style={{ right: 16, bottom: 0, position: 'absolute' }}
              disabled={!selectingCardId || (addCardExeProps.parent === 'grid' && !addCardExeProps.grid)}
              onClick={() => addCardExe({ ...addCardExeProps, selectingCardId, ids, cards })}
            >
              { (addCardExeProps.parent === 'grid' && !addCardExeProps.grid) ? 'グリッドを選択してください' : '選択したカードをシートに追加する' }
            </Button>
          }
        </NoteWrapper>
      </ThemeProvider>
    </>
  );
}

export default Note;
